import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './ElderlyHomePage.css';

const ElderlyHomePage = ({ apiUrl }) => {
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isInCall, setIsInCall] = useState(false);
  
  // Load accessible profiles
  useEffect(() => {
    const loadProfiles = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${apiUrl}/elderly/profiles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setProfiles(response.data.profiles || []);
        setLoading(false);
      } catch (err) {
        console.error('Error loading profiles:', err);
        setError('Unable to load profiles. Please try again.');
        setLoading(false);
      }
    };
    
    loadProfiles();
  }, [apiUrl, token]);
  
  const startCall = useCallback((profile) => {
    setSelectedProfile(profile);
    navigate(`/conversation-realtime/${profile.id}`);
  }, [navigate]);
  
  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);
  
  if (loading) {
    return (
      <div className="elderly-home loading">
        <div className="loading-spinner">
          <span className="spinner-icon">⏳</span>
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (profiles.length === 0) {
    return (
      <div className="elderly-home empty">
        <div className="empty-state">
          <span className="empty-icon">📱</span>
          <h2>No Companions Available Yet</h2>
          <p>Your family member hasn't finished setting up your AI companion yet.</p>
          <p>Please ask them to complete the setup.</p>
          <button className="logout-button" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="elderly-home">
      {/* Simple header */}
      <header className="elderly-header">
        <h1>👋 Hello!</h1>
        <p>Who would you like to talk to today?</p>
      </header>
      
      {/* Profile cards */}
      <div className="profiles-grid">
        {profiles.map((profile) => (
          <div 
            key={profile.id}
            className="profile-card"
            onClick={() => startCall(profile)}
          >
            {/* Photo or placeholder */}
            <div className="profile-photo">
              {profile.photo_url ? (
                <img 
                  src={`${apiUrl}/static/photos/${profile.id}/profile.jpg`} 
                  alt={profile.family_member_name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.classList.add('no-photo');
                  }}
                />
              ) : (
                <span className="photo-placeholder">👤</span>
              )}
            </div>
            
            {/* Name and relationship */}
            <h2 className="profile-name">
              {profile.family_member_name || 'Family Member'}
            </h2>
            <p className="profile-relationship">
              {profile.relationship || 'Family'}
            </p>
            
            {/* Call button */}
            <button className="call-button">
              <span className="call-icon">📞</span>
              <span>Talk to {profile.family_member_name?.split(' ')[0] || 'them'}</span>
            </button>
          </div>
        ))}
      </div>
      
      {/* Simple footer */}
      <footer className="elderly-footer">
        <button className="settings-link" onClick={() => navigate('/personalization')}>
          ⚙️ Settings
        </button>
        <button className="logout-button-small" onClick={handleLogout}>
          🚪 Log Out
        </button>
      </footer>
      
      {error && (
        <div className="error-toast">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}
    </div>
  );
};

export default ElderlyHomePage;
